import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiUser, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';

interface Props {}

function Login(props: Props) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (!formData.username || !formData.password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        {
          name: formData.username,
          password: formData.password,
        }
      );

      // Store token if provided
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      // Redirect to dashboard
      navigate('/');
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Color scheme, all matching Invoice Generator styles (soft indigo/slate with violet accent)
  const MAIN_BG = "bg-gradient-to-br from-[#f1f5fd] via-[#f5f3ff] to-[#e0e7ef]";
  const LOGIN_CARD_BG = "bg-white";
  const PRIMARY_ACCENT = "text-[#6D28D9]"; // violet
  const HOVER_ACCENT = "hover:text-[#4c1d95]";
  const BTN_GRADIENT = "bg-gradient-to-r from-[#6D28D9] to-[#6366f1]"; // indigo/violet
  const BTN_HOVER = "hover:from-[#7C3AED] hover:to-[#4338CA]";
  const LINK_ACCENT = "text-[#6D28D9]";
  const LINK_HOVER = "hover:text-[#4c1d95]";
  const SUBTLE_LABEL = "text-gray-700";
  const DANGER_BG = "bg-red-50 border border-red-200";
  const DANGER_TXT = "text-red-600";

  return (
    <div className={`min-h-screen ${MAIN_BG} flex items-center justify-center p-4 transition-all`}>
      <div className="w-full max-w-md">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#4c1d95] mb-2 tracking-tight">Invoice Generator</h1>
          <p className="text-[#6D28D9]/80 text-lg">Welcome! Sign in to access your invoice workspace.</p>
        </div>

        {/* Login Card */}
        <div className={`${LOGIN_CARD_BG} rounded-2xl shadow-2xl p-8 border border-[#e0e7ef]`}>
          <h2 className={`text-2xl font-semibold text-gray-800 mb-6 text-center ${PRIMARY_ACCENT}`}>Sign In</h2>

          {error && (
            <div className={`mb-4 p-3 rounded-lg ${DANGER_BG}`}>
              <p className={`${DANGER_TXT} text-sm`}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className={`block text-sm font-medium mb-2 ${SUBTLE_LABEL}`}>
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiUser className="h-5 w-5 text-indigo-400" />
                </div>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-[#e0e7ef] rounded-lg focus:ring-2 focus:ring-[#6D28D9] focus:border-transparent outline-none transition"
                  placeholder="Enter your username"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className={`block text-sm font-medium mb-2 ${SUBTLE_LABEL}`}>
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-indigo-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 border border-[#e0e7ef] rounded-lg focus:ring-2 focus:ring-[#6D28D9] focus:border-transparent outline-none transition"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <FiEyeOff className="h-5 w-5 text-indigo-400 hover:text-indigo-700" />
                  ) : (
                    <FiEye className="h-5 w-5 text-indigo-400 hover:text-indigo-700" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className={`text-sm ${LINK_ACCENT} ${LINK_HOVER} font-medium`}
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full ${BTN_GRADIENT} text-white py-3 rounded-lg font-semibold ${BTN_HOVER} transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl`}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <Link
                to="/register"
                className={`${LINK_ACCENT} ${LINK_HOVER} font-semibold`}
              >
                Sign up to generate invoices
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[#b8b6d6] text-sm mt-6">
          © 2024 Invoice Generator. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default Login;

